import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Table from "../../core/pagination/datatable";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import CommonFooter from "../../core/common/footer/commonFooter";
import { Eye, Trash2 } from "feather-icons-react/build/IconComponents";
import { all_routes } from "../../Router/all_routes";
import CommonDeleteModal from "../../core/common/modal/commonDeleteModal";
import { toast } from "react-toastify";
import { getInvoiceList, deleteInvoice } from "../../api/invoiceApi";

const DEFAULT_USER_IMAGE = `${process.env.PUBLIC_URL || ""}/assets/img/by-default.png`;
const IMAGE_BASE = (
  process.env.REACT_APP_API_IMAGE_BASE_URL ||
  "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
).replace(/\/+$/, "");

const isAbsoluteHttp = (url = "") => /^https?:\/\//i.test(url);
const isDataUrl = (url = "") => /^data:image/i.test(url);

const resolveCustomerImage = (record) => {
  const raw =
    record?.customerImage ||
    record?.image ||
    record?.avatar ||
    record?.customer?.image ||
    record?.customer?.avatar ||
    record?.customer?.photo ||
    record?.customer?.profileImage;

  if (!raw) return DEFAULT_USER_IMAGE;

  const baseVal = Array.isArray(raw) ? raw[0] : raw;
  const value =
    typeof baseVal === "object" && baseVal !== null
      ? baseVal.url || baseVal.key || baseVal.path || baseVal.location || baseVal.src || ""
      : baseVal;

  if (!value) return DEFAULT_USER_IMAGE;
  if (isDataUrl(value) || isAbsoluteHttp(value)) return value;

  const normalizedKey = String(value).replace(/^\/+/, "");
  return `${IMAGE_BASE}/${normalizedKey}`;
};

const Invoice = () => {
  const route = all_routes;
  const t = (key) => (window?.translate ? window.translate(key) : key);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    vendorStoreId: localStorage.getItem("storeId") || "",
  });

  const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

  const formatDate = (value) => {
    if (!value) return "--";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "--";
    return d.toLocaleDateString();
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);

      const response = await getInvoiceList({
        page: 1,
        limit: 100,
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.vendorStoreId ? { vendorStoreId: filters.vendorStoreId } : {}),
      });

      const list = Array.isArray(response?.data) ? response.data : [];
      setRows(list);
    } catch (error) {
      toast.error(error?.message || t("Failed to fetch invoices"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const handleRefresh = () => {
    fetchInvoices();
  };

  const handleDeleteInvoice = async () => {
    if (!selectedInvoiceId) return;

    try {
      await deleteInvoice(selectedInvoiceId);
      toast.success(t("Invoice deleted successfully"));
      setSelectedInvoiceId(null);
      fetchInvoices();
    } catch (error) {
      toast.error(error?.message || t("Failed to delete invoice"));
    }
  };

  const data = useMemo(() => rows, [rows]);

  const columns = [
    {
      title: t("Invoice No"),
      dataIndex: "invoiceNo",
      render: (text, record) => (
        <Link to={`${route.invoicedetails}/${record._id}`}>{text || t("N/A")}</Link>
      ),
      sorter: (a, b) =>
        String(a.invoiceNo || "").localeCompare(String(b.invoiceNo || "")),
    },
    {
      title: t("Customer"),
      dataIndex: "customerName",
      render: (text, record) => (
        <div className="d-flex align-items-center">
          <Link to="#" className="avatar avatar-md">
            <img
              src={resolveCustomerImage(record)}
              alt="customer"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DEFAULT_USER_IMAGE;
              }}
            />
          </Link>
          <Link to="#" className="ms-2">
            {text || t("Walk-in Customer")}
          </Link>
        </div>
      ),
      sorter: (a, b) =>
        String(a.customerName || "").localeCompare(String(b.customerName || "")),
    },
    {
      title: t("Due Date"),
      dataIndex: "dueDate",
      render: (value) => formatDate(value),
      sorter: (a, b) => new Date(a.dueDate) - new Date(b.dueDate),
    },
    {
      title: t("Amount"),
      dataIndex: "amount",
      render: (value) => formatMoney(value),
      sorter: (a, b) => Number(a.amount || 0) - Number(b.amount || 0),
    },
    {
      title: t("Paid"),
      dataIndex: "paid",
      render: (value) => formatMoney(value),
      sorter: (a, b) => Number(a.paid || 0) - Number(b.paid || 0),
    },
    {
      title: t("Amount Due"),
      dataIndex: "amountDue",
      render: (value) => formatMoney(value),
      sorter: (a, b) => Number(a.amountDue || 0) - Number(b.amountDue || 0),
    },
    {
      title: window.translate("Status"),
      dataIndex: "status",
      render: (text) => (
        <div>
          {text === "Paid" && (
            <span className="badge badge-soft-success badge-xs shadow-none">
              <i className="ti ti-point-filled me-1"></i>
              {t("Paid")}
            </span>
          )}
          {text === "Unpaid" && (
            <span className="badge badge-soft-danger badge-xs shadow-none">
              <i className="ti ti-point-filled me-1"></i>
              {t("Unpaid")}
            </span>
          )}
          {text === "Overdue" && (
            <span className="badge badge-soft-warning badge-xs shadow-none">
              <i className="ti ti-point-filled me-1"></i>
              {t("Overdue")}
            </span>
          )}
        </div>
      ),
      sorter: (a, b) => String(a.status || "").localeCompare(String(b.status || "")),
    },
    {
      title: "",
      dataIndex: "action",
      render: (_, record) => (
        <div className="edit-delete-action d-flex align-items-center justify-content-center">
          <Link
            className="me-2 p-2 d-flex align-items-center justify-content-between border rounded"
            to={`${route.invoicedetails}/${record._id}`}
          >
            <Eye className="feather-eye" />
          </Link>
          <Link
            className="p-2 d-flex align-items-center justify-content-between border rounded"
            to="#"
            data-bs-toggle="modal"
            data-bs-target="#delete-modal"
            onClick={() => setSelectedInvoiceId(record._id)}
          >
            <Trash2 className="feather-trash-2" />
          </Link>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>{t("Invoices")}</h4>
                <h6>{t("Manage your stock invoices")}</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <li>
                <Link to="#" onClick={handleRefresh}>
                  <RefreshIcon />
                </Link>
              </li>
              <CollapesIcon />
            </ul>
          </div>

          <div className="card table-list-card no-search">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set d-flex align-items-center gap-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("Search invoice no or customer")}
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      search: e.target.value,
                    }))
                  }
                  style={{ minWidth: 260 }}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={fetchInvoices}
                >
                  {t("Search")}
                </button>
              </div>

              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {filters.status ? t(filters.status) : t("Status")}
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item rounded-1"
                        onClick={() => setFilters((prev) => ({ ...prev, status: "" }))}
                      >
                        {t("All")}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item rounded-1"
                        onClick={() => setFilters((prev) => ({ ...prev, status: "Paid" }))}
                      >
                        {t("Paid")}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item rounded-1"
                        onClick={() => setFilters((prev) => ({ ...prev, status: "Unpaid" }))}
                      >
                        {t("Unpaid")}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item rounded-1"
                        onClick={() => setFilters((prev) => ({ ...prev, status: "Overdue" }))}
                      >
                        {t("Overdue")}
                      </Link>
                    </li>
                  </ul>
                </div>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={fetchInvoices}
                >
                  {t("Apply")}
                </button>
              </div>
            </div>

            <div className="card-body">
              <div className="table-responsive">
                {loading ? (
                  <div className="text-center py-4">{t("Loading")}...</div>
                ) : (
                  <Table columns={columns} dataSource={data} />
                )}
              </div>
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>

      <div
        className="modal fade"
        id="delete-modal"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content p-4">
            <div className="modal-body text-center">
              <h4>{t("Delete Invoice")}</h4>
              <p>{t("Are you sure you want to delete this invoice?")}</p>
              <div className="d-flex justify-content-center gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  {t("Cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  data-bs-dismiss="modal"
                  onClick={handleDeleteInvoice}
                >
                  {t("Delete")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CommonDeleteModal />
    </div>
  );
};

export default Invoice;
